import type { SupabaseClient } from "@supabase/supabase-js";
import { type CaptureItem, parseCaptureItem } from "./items-schema";

interface CaptureItemDbRow {
  id: string;
  capture_id: string;
  type: string;
  text: string;
  summary: string | null;
  speaker: string | null;
  position_ms: number | null;
  // Drapeaux qualité (Lot 0.B). Optionnels côté row pour rétrocompat avec
  // les tests existants qui mockent des lignes minimales — les défauts du
  // schema Zod prennent le relais.
  needs_review?: boolean | null;
  summary_factual_warning?: boolean | null;
  edited_by_human?: boolean | null;
  not_anchored?: boolean | null;
  recovered_at?: string | null;
  created_at: string;
  updated_at: string;
  item_tags?: Array<{ tag: string }>;
}

function normalizeIso(value: string): string {
  return new Date(value).toISOString();
}

function mapRowToItem(row: CaptureItemDbRow): CaptureItem {
  return parseCaptureItem({
    id: row.id,
    captureId: row.capture_id,
    type: row.type,
    text: row.text,
    summary: row.summary,
    speaker: row.speaker,
    positionMs: row.position_ms,
    tags: (row.item_tags ?? []).map((t) => t.tag),
    needsReview: row.needs_review ?? false,
    summaryFactualWarning: row.summary_factual_warning ?? false,
    editedByHuman: row.edited_by_human ?? false,
    notAnchored: row.not_anchored ?? false,
    recoveredAt: row.recovered_at ? normalizeIso(row.recovered_at) : null,
    createdAt: normalizeIso(row.created_at),
    updatedAt: normalizeIso(row.updated_at),
  });
}

export type FetchItemsResult = { ok: true; data: CaptureItem[] } | { ok: false; error: string };

export async function fetchItemsForCapture(
  client: SupabaseClient,
  captureId: string,
): Promise<FetchItemsResult> {
  // position_ms NULLS LAST puis created_at en cas d'égalité.
  // Filtre les items soft-deleted (Lot 0.B / ADR 0015 §2).
  const { data, error } = await client
    .from("capture_items")
    .select("*, item_tags(tag)")
    .eq("capture_id", captureId)
    .is("deleted_at", null)
    .order("position_ms", { ascending: true, nullsFirst: false });

  if (error) return { ok: false, error: error.message };

  const rows = (data ?? []) as CaptureItemDbRow[];
  const items: CaptureItem[] = [];
  for (const row of rows) {
    try {
      items.push(mapRowToItem(row));
    } catch {
      // row malformée : on l'ignore plutôt que faire planter le rendu
    }
  }
  return { ok: true, data: items };
}

// ---------------------------------------------------------------------------
// Batch « accueil » : items de PLUSIEURS captures
//
// Cette requête a produit un bogue P1 le 2026-09-22 : elle chargeait les items
// de toutes les captures prêtes d'un seul coup, et PostgREST plafonne une
// réponse à 1000 lignes SANS le dire (statut 200, tableau partiel, en-tête
// `content-range: 0-999/*`). Avec 1150 items en base, les captures dont les
// items tombaient après la coupure affichaient « Aucun item détecté ».
// Une troncature était lue comme un fait.
//
// Deux réponses, indissociables :
//   1. paginer par `range()` jusqu'à ÉPUISEMENT (plus de coupure) ;
//   2. lire le total renvoyé par le serveur (`count` = le `content-range`) et
//      dire explicitement, via `complete`, si la photo est entière. Une photo
//      partielle vaut « je ne sais pas », jamais « il n'y a rien ».
// ---------------------------------------------------------------------------

// Projection MINIMALE : la liste d'accueil ne se sert que du nombre d'items par
// capture et des drapeaux qualité (badge « N à corriger », cf. to-correct.ts).
// Ni `text`, ni `summary`, ni la jointure `item_tags` — inutiles ici et très
// coûteux en egress sur plus de mille lignes. `created_at` (un horodatage par
// ligne) date la fin de l'extraction : il borne l'attente de la synthèse
// (SIG-261002-082836, `secondsSinceNewestItem`).
export const ITEM_SUMMARY_COLUMNS =
  "id, capture_id, needs_review, not_anchored, summary_factual_warning, created_at";

// Taille de page demandée, volontairement sous le plafond serveur (1000).
export const ITEMS_PAGE_SIZE = 500;
// Garde-fou anti-boucle : un serveur qui ne progresse pas ne doit pas figer
// l'accueil. Atteindre ce plafond ⇒ `complete: false`, jamais un faux « vide ».
export const ITEMS_MAX_PAGES = 40;

// Vue allégée d'un item, suffisante pour la liste d'accueil. `CaptureItem` la
// satisfait structurellement : le poll mono-capture de la carte peut donc
// continuer d'alimenter le même état sans conversion.
export interface CaptureItemSummary {
  id: string;
  captureId: string;
  needsReview: boolean;
  notAnchored: boolean;
  summaryFactualWarning: boolean;
  createdAt?: string;
}

interface CaptureItemSummaryRow {
  id?: unknown;
  capture_id?: unknown;
  needs_review?: unknown;
  not_anchored?: unknown;
  summary_factual_warning?: unknown;
  created_at?: unknown;
}

function mapRowToSummary(row: CaptureItemSummaryRow): CaptureItemSummary | null {
  if (typeof row.id !== "string" || typeof row.capture_id !== "string") return null;
  return {
    id: row.id,
    captureId: row.capture_id,
    needsReview: row.needs_review === true,
    notAnchored: row.not_anchored === true,
    summaryFactualWarning: row.summary_factual_warning === true,
    ...(typeof row.created_at === "string" ? { createdAt: row.created_at } : {}),
  };
}

// Range une page de lignes dans l'accumulateur indexé par capture_id. Une row
// malformée est ignorée plutôt que de faire planter le rendu de la liste.
function groupIntoMap(
  rows: CaptureItemSummaryRow[],
  byCaptureId: Map<string, CaptureItemSummary[]>,
): void {
  for (const row of rows) {
    const item = mapRowToSummary(row);
    if (!item) continue;
    const arr = byCaptureId.get(item.captureId);
    if (arr) arr.push(item);
    else byCaptureId.set(item.captureId, [item]);
  }
}

export type FetchItemsBatchResult =
  | {
      ok: true;
      data: Map<string, CaptureItemSummary[]>;
      /** La photo est-elle ENTIÈRE ? `false` = réponse partielle : l'appelant
       *  doit traiter ça comme une absence d'information, pas d'items. */
      complete: boolean;
    }
  | { ok: false; error: string };

// Charge les items de plusieurs captures, page par page, jusqu'à épuisement.
// Retourne une Map indexée par capture_id + le drapeau `complete`.
export async function fetchItemsForCaptures(
  client: SupabaseClient,
  captureIds: string[],
): Promise<FetchItemsBatchResult> {
  if (captureIds.length === 0) return { ok: true, data: new Map(), complete: true };

  const byCaptureId = new Map<string, CaptureItemSummary[]>();
  let collected = 0;
  // Total annoncé par le serveur (dérivé de `content-range` par supabase-js).
  // `null` tant qu'aucune réponse ne l'a porté.
  let serverTotal: number | null = null;
  let exhausted = false;

  for (let page = 0; page < ITEMS_MAX_PAGES; page++) {
    const from = collected;
    // Filtre les items soft-deleted (Lot 0.B / ADR 0015 §2).
    // Ordre TOTAL et déterministe : `position_ms` est NULL sur 100 % des items
    // en base, donc trier dessus ne trie rien et rend la pagination instable.
    const { data, error, count } = await client
      .from("capture_items")
      .select(ITEM_SUMMARY_COLUMNS, { count: "exact" })
      .in("capture_id", captureIds)
      .is("deleted_at", null)
      .order("created_at", { ascending: true })
      .order("id", { ascending: true })
      .range(from, from + ITEMS_PAGE_SIZE - 1);

    if (error) return { ok: false, error: error.message };
    if (typeof count === "number") serverTotal = count;

    const rows = (data ?? []) as CaptureItemSummaryRow[];
    groupIntoMap(rows, byCaptureId);
    collected += rows.length;

    // Page vide = il n'y a plus rien à lire. C'est la SEULE preuve d'épuisement
    // qui ne dépende pas de la taille de page : une page plus courte que
    // demandée signifie simplement que le serveur plafonne plus bas que nous,
    // pas qu'on a tout vu (c'est exactement le piège d'origine).
    if (rows.length === 0) {
      exhausted = true;
      break;
    }
    if (serverTotal !== null && collected >= serverTotal) {
      exhausted = true;
      break;
    }
  }

  const complete = serverTotal !== null ? collected >= serverTotal : exhausted;
  return { ok: true, data: byCaptureId, complete };
}
